// src/hooks/usePatient.tsx
/**
 * Hook for fetching patient info (name, id, etc.)
 */
import { fetchData, deleteData } from "api/apiHelpers";
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotification } from "hooks/useNotification";
import logger from "utils/logger";
import { useTranslation } from "../i18n";

interface PatientInfo {
  id: string | number;
  name: string;
  anonymized_name?: string;  // Backend-provided anonymized name
  patient_url?: string;       // Backend-provided URL for exports
  environment_names?: string[]; // Environment names for display
  age?: number;
  domains?: string[];
  external_id?: string;
  legacy_id?: string;
  /** Backend-computed: whether THIS caller may reveal raw name + external_id
   *  for this patient (drives the eye icon + edit pencil). */
  can_reveal_pii?: boolean;
  /** DynamicModel.modified_at (auto_now). The ROW's last write time, not a
   *  clinical event time; the patient header labels it "Updated" for that
   *  reason. NOTE: `updated_at` exists on the model too and is NULL on every patient;
   *  `modified_at` is the column that is actually written. */
  modified_at?: string;
}

const usePatient = () => {
  const { id: patientId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notification = useNotification();
  const { t } = useTranslation();
  
  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch patient info
  useEffect(() => {
    const fetchPatient = async () => {
      if (!patientId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await fetchData<PatientInfo>(`/patients/${patientId}/`);
        setPatient(data);
      } catch (err: any) {
        logger.error("Error fetching patient:", err);
        setError(err?.message || "Failed to fetch patient");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatient();
  }, [patientId]);

  // Delete patient
  const deletePatient = useCallback(async () => {
    if (!patientId) return;
    
    await deleteData(`/patients/${patientId}/`);
    notification.success(t("patient.deleted_success", "Patient deleted successfully"));
    navigate("/dashboard");
  }, [patientId, notification, navigate, t]);

  // Refresh patient data
  const refreshPatient = useCallback(async () => {
    if (!patientId) return;
    
    try {
      const data = await fetchData<PatientInfo>(`/patients/${patientId}/`);
      setPatient(data);
    } catch (err: any) {
      logger.error("Error refreshing patient:", err);
    }
  }, [patientId]);

  return {
    patient,
    patientId,
    isLoading,
    error,
    deletePatient,
    refreshPatient,
  };
};

export default usePatient;
